import { api } from "./client";

const BASE = "/games/wc";

// ── Season ──────────────────────────────────────────────────────────────────

export async function getWcSeasons() {
  const resp = await api.get(`${BASE}/admin/seasons/`);
  return Array.isArray(resp.data) ? resp.data : [];
}

// ── Pools ────────────────────────────────────────────────────────────────────

export async function getMyWcPools(seasonId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/mine`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function getWcPoolWindow(seasonId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/pool-window`);
  return resp.data || {};
}

export async function getAllWcPools(seasonId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function createWcPool(seasonId, body) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/`, body);
  return resp.data;
}

export async function joinWcPool(seasonId, poolId) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/join`);
  return resp.data;
}

export async function getWcPool(seasonId, poolId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/${poolId}/`);
  return resp.data;
}

export async function updateWcPool(seasonId, poolId, body) {
  const resp = await api.patch(`${BASE}/seasons/${seasonId}/pools/${poolId}/`, body);
  return resp.data;
}

export async function removeWcMember(seasonId, poolId, userId) {
  const resp = await api.delete(`${BASE}/seasons/${seasonId}/pools/${poolId}/members/${userId}`);
  return resp.data;
}

export async function toggleWcMemberPaid(seasonId, poolId, userId, paid) {
  const resp = await api.patch(`${BASE}/seasons/${seasonId}/pools/${poolId}/members/${userId}/paid`, { paid });
  return resp.data;
}

export async function toggleWcCoAdmin(seasonId, poolId, userId, isCoAdmin) {
  const resp = await api.patch(`${BASE}/seasons/${seasonId}/pools/${poolId}/members/${userId}/co-admin`, { is_co_admin: isCoAdmin });
  return resp.data;
}

export async function sendWcInvites(seasonId, poolId, emails, message, subject, mode) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/invite`, { emails, message, subject, mode }, { timeout: 120000 });
  return resp.data;
}

// ── Draft ────────────────────────────────────────────────────────────────────

export async function randomizeDraft(seasonId, poolId) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/randomize-draft`);
  return resp.data;
}

export async function getWcDraftState(seasonId, poolId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/${poolId}/draft/`);
  return resp.data;
}

export async function makeWcPick(seasonId, poolId, teamId, onBehalfOfUserId = null) {
  const body = { team_id: teamId };
  if (onBehalfOfUserId != null) body.on_behalf_of_user_id = onBehalfOfUserId;
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/draft/pick`, body);
  return resp.data;
}

export async function setWcAutodraft(seasonId, poolId, enabled) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/draft/autodraft`, { enabled });
  return resp.data;
}

// ── Leaderboard / Fixtures ───────────────────────────────────────────────────

export async function getWcLeaderboard(seasonId, poolId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/${poolId}/leaderboard`);
  return resp.data;
}

export async function getWcFixtures(seasonId, poolId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/${poolId}/fixtures`);
  return Array.isArray(resp.data) ? resp.data : [];
}

// ── Payouts ──────────────────────────────────────────────────────────────────

export async function getWcPayouts(seasonId, poolId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/${poolId}/payouts/`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function saveWcPayouts(seasonId, poolId, payouts) {
  const resp = await api.put(`${BASE}/seasons/${seasonId}/pools/${poolId}/payouts/`, { payouts });
  return Array.isArray(resp.data) ? resp.data : [];
}

// ── Swap ─────────────────────────────────────────────────────────────────────

export async function getWcSwapEligible(seasonId, poolId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/${poolId}/swap/eligible`);
  return Array.isArray(resp.data) ? resp.data : [];
}

export async function applyWcSwap(seasonId, poolId, removeTeamId, addTeamId) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/swap/`, {
    remove_team_id: removeTeamId,
    add_team_id: addTeamId,
  });
  return resp.data;
}

// ── Smack Board ───────────────────────────────────────────────────────────────

export async function getWcSmackMessages(poolId, seasonId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/${poolId}/smack/`);
  return resp.data;
}

export async function postWcSmackMessage(poolId, body, seasonId) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/smack/`, { body });
  return resp.data;
}

export async function getWcSmackReplies(poolId, messageId, seasonId) {
  const resp = await api.get(`${BASE}/seasons/${seasonId}/pools/${poolId}/smack/${messageId}/replies`);
  return resp.data;
}

export async function postWcSmackReply(poolId, messageId, body, seasonId) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/smack/${messageId}/replies`, { body });
  return resp.data;
}

export async function reactWcSmack(poolId, messageId, reaction, seasonId) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/smack/${messageId}/react`, { reaction });
  return resp.data;
}

export async function deleteWcSmackMessage(poolId, messageId, seasonId) {
  const resp = await api.delete(`${BASE}/seasons/${seasonId}/pools/${poolId}/smack/${messageId}`);
  return resp.data;
}

export async function sendWcRecapTest(seasonId, poolId) {
  const resp = await api.post(`${BASE}/seasons/${seasonId}/pools/${poolId}/recap-test`, null, { timeout: 30000 });
  return resp.data;
}
